import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, Button, Form, Input, Radio, RadioChangeEvent } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertType, useAlert } from "../provider/AlertProvider";
import { useAuthState } from "../provider/AuthProvider";
import AddressMaker, { AddressType } from "../components/AddressMaker";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";

import * as TypeDTO from "../commons/typeDTO";
import * as Request from "../commons/request";
import * as String from "../commons/string";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";

import PhoneCertification from "../components/PhoneCertification";

function UpdateMember() {
    const location = useLocation();
    const navigate = useNavigate();
    const userDetails = useAuthState();
    const alert = useAlert();
    const [form] = Form.useForm();

    const [userInfo, setUserInfo] = useState<TypeDTO.UserDto>();
    const [member, setMember] = useState("INDIVIDUAL");
    const [userName, setUserName] = useState("");
    const [certificationResult, setCertificationResult] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [userAddress, setUserAddress] = useState<AddressType>();
    const [disableInputName, setDisableInputName] = useState(false);

    useEffect(() => {
        const state = location.state as TypeDTO.UserDto;
        state ? setUserInfo(state) : requestMemberInfo();
    }, [location.state]);

    const requestAxiosMemberInfo = async () => {
        if (userDetails === null) return null;
        if (userDetails?.token === null) return null;

        const response = await utilAxiosWithAuth().get(Request.MEMBER_INFO_URL);
        return response.data;
    };

    const { loading: loadingMembereInfo, error: errorMemberInfo, data: resultMemberInfo, execute: requestMemberInfo } = useAsyncAxios(requestAxiosMemberInfo);

    useEffect(() => {
        if (!resultMemberInfo) return;

        console.log("resultMemberInfo", resultMemberInfo);
        setUserInfo(resultMemberInfo.user);
    }, [resultMemberInfo]);

    useEffect(() => {
        if (!errorMemberInfo) return;

        console.log("errorMemberInfo", errorMemberInfo);
    }, [errorMemberInfo]);

    useEffect(() => {
        if (!userInfo) return;

        setMember(userInfo?.userType === "INDIVIDUAL" ? "INDIVIDUAL" : "CORPORATE");
        setUserName(userInfo.name);
        setPhoneNumber(userInfo.phone);
        setUserAddress({
            address: userInfo.address,
            addressDetails: userInfo.addressDetails,
        });

        form.setFieldsValue({
            fullAddress: {
                address: userInfo.address,
                addressDetails: userInfo.addressDetails,
            },
        });

        form.setFieldsValue({ userType: "CORPORATE" });
        form.setFieldsValue(userInfo);
    }, [userInfo]);

    const requestAxiosUpdateUser = async (data: TypeDTO.SignupDto) => {
        const response = await utilAxiosWithAuth().put(Request.MEMBER_INFO_URL, data);
        return response.data;
    };

    const { loading: loadingUpdateUser, error: errorUpdateUser, data: resultUpdateUser, execute: requestUpdateUser } = useAsyncAxios(requestAxiosUpdateUser);

    useEffect(() => {
        if (!resultUpdateUser) return;

        navigate(Common.PAGE_MYPAGE, {
            replace: true,
        });

        alert.setAlert(AlertType.SUCCESS, "회원정보 수정 성공", "회원정보를 수정하였습니다.");
    }, [resultUpdateUser]);

    useEffect(() => {
        if (!errorUpdateUser) return;

        console.log("errorUser", errorUpdateUser);
        alert.setAlert(errorUpdateUser.response?.data?.code, "회원정보 수정 실패", errorUpdateUser.response?.data?.message);
    }, [errorUpdateUser]);

    const onChangedMemberType = (e: RadioChangeEvent) => {
        setMember(e.target.value);
    };

    const checkIndividualType = () => {
        if (member === "INDIVIDUAL") return true;
        return false;
    };

    const validateCompanyRegistrationNo = useCallback((_: any, value: string) => {
        const valueMap = value
            .replace(/-/gi, "")
            .split("")
            .map((item) => {
                return parseInt(item, 10);
            });

        if (valueMap.length !== 10) return Promise.reject(new Error(String.msg_company_registration_no_rule));

        const multiply = new Array<number>(1, 3, 7, 1, 3, 7, 1, 3, 5);
        let checksum = 0;

        for (let i = 0; i < multiply.length; ++i) {
            checksum += multiply[i] * valueMap[i];
        }
        checksum += Math.floor((multiply[8] * valueMap[8]) / 10);

        if (Math.floor(valueMap[9]) === (10 - (checksum % 10)) % 10) return Promise.resolve();
        else return Promise.reject(new Error(String.msg_company_registration_no_rule));
    }, []);

    const handleCertificationResult = (success: boolean, phoneNumber: string) => {
        setPhoneNumber(phoneNumber);
        setCertificationResult(success);

        setDisableInputName(success);
    };

    const onFinish = (values: any) => {
        delete values["confirm"];
        delete values["fullAddress"];
        const data: TypeDTO.SignupDto = {
            companyName: "",
            ...values,
            phone: phoneNumber,
            address: userAddress?.address,
            addressDetails: userAddress?.addressDetails,
        };
        console.log("Received values of form: ", data);
        requestUpdateUser(data);
    };

    return (
        <Row className="login py-4" align="middle" justify="center">
            <div className="loginCard">
                <Card bordered={false}>
                    <div className="text-center mb-4">
                        <h3>회원 정보 수정</h3>
                    </div>
                    <Form form={form} onFinish={onFinish} layout="vertical">
                        <Form.Item label="회원 종류" name="userType" initialValue={member}>
                            <Radio.Group disabled={true} onChange={onChangedMemberType}>
                                <Radio value="INDIVIDUAL">개인 회원</Radio>
                                <Radio value="CORPORATE">법인 회원</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            name="name"
                            label={checkIndividualType() ? "이름" : "이름(대표자)"}
                            rules={[
                                {
                                    required: true,
                                    message: "이름을 입력해 주세요.",
                                },
                            ]}
                        >
                            <Input
                                className="login-input"
                                placeholder="휴대전화 명의자 이름을 입력해 주세요."
                                disabled={disableInputName}
                                value={userName}
                                onChange={(e) => setUserName(e.target.value)}
                            />
                        </Form.Item>

                        {checkIndividualType() === false ? (
                            <Form.Item name="companyName" label="회사명">
                                <Input className="login-input" />
                            </Form.Item>
                        ) : null}

                        {checkIndividualType() === false ? (
                            <Form.Item
                                name="companyRegistrationNo"
                                label="사업자 등록번호"
                                rules={[
                                    {
                                        required: true,
                                        message: String.msg_company_registration_no_must,
                                    },
                                    {
                                        pattern: new RegExp(/^([-]?[1-9][0-9]*|0)$/),
                                        message: String.msg_company_registration_no_only_number,
                                    },
                                    {
                                        validator: validateCompanyRegistrationNo,
                                    },
                                ]}
                            >
                                <Input className="login-input" placeholder="사업자 등록번호(- 미포함)" />
                            </Form.Item>
                        ) : null}

                        <Form.Item
                            name="email"
                            label={String.email}
                            rules={[
                                {
                                    required: true,
                                    message: "이메일을 입력해 주세요.",
                                },
                                {
                                    validator: (_, value) => Utils.validateEmail(true, true, value),
                                },
                            ]}
                        >
                            <Input
                                className="login-input"
                                onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toLowerCase())}
                            />
                        </Form.Item>

                        <Form.Item
                            name="phone"
                            label={String.phone}
                            required={true}
                            rules={[
                                () => ({
                                    validator(_, value) {
                                        if (certificationResult) {
                                            return Promise.resolve();
                                        }

                                        return Promise.reject(new Error("휴대전화 본인 인증을 해 주세요."));
                                    },
                                }),
                            ]}
                        >
                            <PhoneCertification value={phoneNumber} userName={userName} onChangedResult={handleCertificationResult} />
                        </Form.Item>

                        <h6>추가정보</h6>
                        <Form.Item
                            className="login-group"
                            name="fullAddress"
                            label={String.address}
                            rules={[
                                () => ({
                                    validator(_, value) {
                                        if (userAddress !== undefined && userAddress.address.length > 0) {
                                            return Promise.resolve();
                                        }

                                        return Promise.reject(new Error("주소를 입력해 주세요."));
                                    },
                                }),
                            ]}
                        >
                            <AddressMaker value={userAddress} onChangeAddress={(address) => setUserAddress(address)} />
                        </Form.Item>

                        <Form.Item>
                            <Row>
                                <Col className="pe-1" span={12}>
                                    <Button
                                        block
                                        type="default"
                                        size="large"
                                        onClick={() =>
                                            navigate(Common.PAGE_MYPAGE, {
                                                replace: true,
                                            })
                                        }
                                    >
                                        {String.cancel}
                                    </Button>
                                </Col>
                                <Col className="ps-1" span={12}>
                                    <Button block type="primary" htmlType="submit" size="large">
                                        {String.save}
                                    </Button>
                                </Col>
                            </Row>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </Row>
    );
}

export default UpdateMember;
